import { useState, useEffect } from 'react';
import {
  listarPreferencias,
  alterarPreferencia,
  PreferenciaNotificacao,
} from '../../services/notificacaoService';
import './NotificacoesConfig.css';

export default function NotificacoesConfig() {
  const [preferencias, setPreferencias] = useState<PreferenciaNotificacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmacao, setConfirmacao] = useState<{ projetoId: string; mensagem: string } | null>(
    null,
  );
  const [toggling, setToggling] = useState<string | null>(null);

  useEffect(() => {
    fetchPreferencias();
  }, []);

  async function fetchPreferencias() {
    try {
      setLoading(true);
      setError('');
      const data = await listarPreferencias();
      setPreferencias(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar preferências.');
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle(projetoId: string, novoEstado: boolean) {
    setToggling(projetoId);
    setConfirmacao(null);

    try {
      await alterarPreferencia(projetoId, novoEstado);

      setPreferencias((prev) =>
        prev.map((p) => (p.projetoId === projetoId ? { ...p, notificacoesAtivas: novoEstado } : p)),
      );

      const mensagem = novoEstado
        ? 'Notificações ativadas para este projeto.'
        : 'Notificações desativadas para este projeto.';

      setConfirmacao({ projetoId, mensagem });

      setTimeout(() => {
        setConfirmacao((atual) => (atual?.projetoId === projetoId ? null : atual));
      }, 3000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao alterar preferência.');
    } finally {
      setToggling(null);
    }
  }

  if (loading) {
    return (
      <div className="notificacoes-config">
        <h1 className="notificacoes-config__title">Notificações</h1>
        <p className="notificacoes-config__loading">Carregando...</p>
      </div>
    );
  }

  if (error && preferencias.length === 0) {
    return (
      <div className="notificacoes-config">
        <h1 className="notificacoes-config__title">Notificações</h1>
        <p className="notificacoes-config__error">{error}</p>
      </div>
    );
  }

  if (preferencias.length === 0) {
    return (
      <div className="notificacoes-config">
        <h1 className="notificacoes-config__title">Notificações</h1>
        <div className="notificacoes-config__empty">
          <span className="notificacoes-config__empty-icon">🔔</span>
          <p>Você não possui preferências de notificação configuradas.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notificacoes-config">
      <h1 className="notificacoes-config__title">Notificações</h1>
      <p className="notificacoes-config__subtitle">
        Escolha para quais projetos deseja receber notificações por e-mail sobre atualizações de
        progresso.
      </p>

      {error && <p className="notificacoes-config__error">{error}</p>}

      <ul className="notificacoes-config__list">
        {preferencias.map((pref) => (
          <li key={pref.projetoId} className="notificacoes-config__item">
            <div className="notificacoes-config__item-info">
              <span className="notificacoes-config__item-nome">{pref.nomeProjeto}</span>
              <span className="notificacoes-config__item-status">
                {pref.notificacoesAtivas ? 'Ativadas' : 'Desativadas'}
              </span>
            </div>

            <label className="notificacoes-config__toggle">
              <input
                type="checkbox"
                className="notificacoes-config__toggle-input"
                checked={pref.notificacoesAtivas}
                disabled={toggling === pref.projetoId}
                onChange={() => handleToggle(pref.projetoId, !pref.notificacoesAtivas)}
                aria-label={`Notificações para ${pref.nomeProjeto}`}
              />
              <span className="notificacoes-config__toggle-slider" />
            </label>

            {confirmacao?.projetoId === pref.projetoId && (
              <span className="notificacoes-config__confirmacao">{confirmacao.mensagem}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
